'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import Image from 'next/image';
import { AlertCircle, Check, CheckCircle, Cloud, CloudOff, Loader2, Plane, RefreshCw, Send, X } from 'lucide-react';
import { prepareAttendanceOfflineAssets } from '@/lib/offline/prepareAttendanceOfflineAssets';
import { getQueuedOfflineFormSubmissions, type QueuedSubmission } from '@/lib/offline/operationalFormsStore';
import { loadQueuedExpenseActions, type QueuedExpenseAction } from '@/lib/operations/expenseOfflineActions';
import { useModalState } from '@/contexts/ModalStateContext';
import { useSyncStatus } from '@/contexts/SyncStatusContext';
import { useMasterSyncOfflinePreparation, type OfflinePreparationProgressState } from '@/hooks/useMasterSyncOfflinePreparation';
import type { MasterSyncNetworkStatus } from '@/lib/masterSyncNetworkStatus';
import masterSync from '@/lib/masterSync';
import { networkState } from '@/lib/networkState';
import { notify } from '@/lib/notifications';
import { isCapacitor } from '@/lib/platformUtils';
import { isStandalonePwa } from '@/lib/pwaAppIdentity';
import { MOBILE_SAFE_AREA_BOTTOM, MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';

const AUTO_OFFLINE_PREP_STORAGE_PREFIX = 'ta_auto_offline_prep_v1';
const AUTO_OFFLINE_PREP_MODE_STORAGE_PREFIX = 'ta_auto_offline_prep_mode_v1';
const AUTO_OFFLINE_PREP_DELAY_MS = isCapacitor() ? 4000 : 8000;
const AUTO_OFFLINE_PREP_FRESHNESS_MS = 30 * 60 * 1000;
const AUTO_OFFLINE_PREP_RETRY_MS = 5 * 60 * 1000;
const SMARTTASK_STARTUP_ACTIVITY_STORAGE_KEY = 'ta_smarttask_startup_active_until_v1';
const OFFLINE_PREP_TRANSIENT_RETRY_DELAY_MS = 1200;

export type OfflinePreparationMode = 'auto' | 'manual';

export type MasterSyncModalNetworkStatus = MasterSyncNetworkStatus;

type OfflinePreparationDetailIndex = {
  attendance?: number;
  workItems?: number;
  forms?: number;
  vehicles?: number;
  pages?: number;
  faceModels?: number;
};

interface OfflinePreparationSummary {
  preparingSubtitle: string;
  readySubtitle: string;
}

interface OfflinePreparationDebugSummary {
  attendanceData: number;
  workItems: number;
  forms: number;
  vehicles: number;
  pages: number;
  faceModels: number;
}

interface OfflinePreparationState {
  lastAttemptAt: number;
  completedAt: number;
  lastResult: 'success' | 'error' | null;
}

function isTransientOfflinePreparationError(message: string | null | undefined): boolean {
  if (!message) {
    return false;
  }

  const normalized = String(message).toLowerCase();
  return normalized.includes('sync already in progress')
    || normalized.includes('offline preparation already in progress');
}

function serializeOfflinePrepLog(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function isSmartTaskStartupActive(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const rawValue = window.sessionStorage.getItem(SMARTTASK_STARTUP_ACTIVITY_STORAGE_KEY);
    if (!rawValue) {
      return false;
    }

    const activeUntil = Number(rawValue);
    if (!Number.isFinite(activeUntil) || activeUntil <= Date.now()) {
      window.sessionStorage.removeItem(SMARTTASK_STARTUP_ACTIVITY_STORAGE_KEY);
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

export interface UseMasterSyncModalOptions {
  mounted: boolean;
  isAuthenticated: boolean;
  isOnDashboard: boolean;
  networkStatus: MasterSyncModalNetworkStatus;
  userId?: number | string | null;
  autoPreparationClientId?: number | null;
  autoDelayMs?: number;
  autoFreshnessMs?: number;
  autoRetryMs?: number;
}

export interface MasterSyncModalController {
  canPrepareOffline: boolean;
  isOpen: boolean;
  status: 'idle' | 'preparing' | 'ready' | 'error';
  progress: OfflinePreparationProgressState | null;
  offlinePreparationMode: OfflinePreparationMode;
  openModal: () => void;
  closeModal: () => void;
  startPreparation: () => Promise<boolean>;
  setOfflinePreparationMode: (mode: OfflinePreparationMode) => void;
  startDisabled: boolean;
  startLabel: string;
  startTitle: string;
  preparingSubtitle: string;
  readySubtitle: string;
}

export interface MasterSyncModalProps {
  controller: MasterSyncModalController;
  accentColor?: string;
  title?: string;
  errorSubtitle?: string;
}

export interface MasterSyncPreparationModalProps {
  isOpen: boolean;
  status: 'idle' | 'preparing' | 'ready' | 'error';
  progress: OfflinePreparationProgressState | null;
  offlinePreparationMode?: OfflinePreparationMode;
  onClose: () => void;
  onStart?: () => void;
  onRetry?: () => void;
  onOfflinePreparationModeChange?: (mode: OfflinePreparationMode) => void;
  startDisabled?: boolean;
  startLabel?: string;
  startTitle?: string;
  accentColor?: string;
  title?: string;
  preparingSubtitle?: string;
  readySubtitle?: string;
  errorSubtitle?: string;
}

export interface MasterSyncStatusBadgeProps {
  className?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

export interface MasterSyncUploadProgressPanelProps {
  className?: string;
  /** `banner` = strip under app header; `card` = rounded block inside MasterSync modal */
  variant?: 'banner' | 'card';
}

export interface MasterSyncProgressBarProps {
  className?: string;
}

export interface MasterSyncOfflineBannerProps {
  className?: string;
}

function getOfflinePreparationDebugSummary(progress: OfflinePreparationProgressState | null): OfflinePreparationDebugSummary | null {
  if (!progress?.details?.length) {
    return null;
  }

  const summary: OfflinePreparationDebugSummary = {
    attendanceData: 0,
    workItems: 0,
    forms: 0,
    vehicles: 0,
    pages: 0,
    faceModels: 0,
  };

  progress.details.forEach((detail) => {
    const count = Number(detail.count || 0);

    switch (detail.label) {
      case 'Attendance data':
        summary.attendanceData = count;
        break;
      case 'Assigned work items':
        summary.workItems = count;
        break;
      case 'Operational forms':
        summary.forms = count;
        break;
      case 'Fleet vehicles':
        summary.vehicles = count;
        break;
      case 'Attendance pages':
        summary.pages = count;
        break;
      case 'Face recognition models':
        summary.faceModels = count;
        break;
      default:
        break;
    }
  });

  return summary;
}

function useOutgoingQueue(isOpen: boolean) {
  const [formSubmissions, setFormSubmissions] = useState<QueuedSubmission[]>([]);
  const [expenseActions, setExpenseActions] = useState<QueuedExpenseAction[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const [forms, expenses] = await Promise.all([
        getQueuedOfflineFormSubmissions().catch(() => []),
        loadQueuedExpenseActions().catch(() => []),
      ]);
      setFormSubmissions(forms);
      setExpenseActions(expenses);
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const totalCount = formSubmissions.length + expenseActions.length;

  return { formSubmissions, expenseActions, loading, totalCount, refresh };
}

function OutgoingQueueSection({
  formSubmissions,
  expenseActions,
  loading,
  accentColor,
}: {
  formSubmissions: QueuedSubmission[];
  expenseActions: QueuedExpenseAction[];
  loading: boolean;
  accentColor: string;
}) {
  const totalCount = formSubmissions.length + expenseActions.length;

  if (loading && totalCount === 0) {
 return (
      <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading outgoing queue…</span>
        </div>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <CheckCircle className="h-4 w-4" style={{ color: accentColor }} />
          <span>No pending items — outgoing queue is empty.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-2">
      <div className="flex items-center gap-2">
        <Send className="h-4 w-4" style={{ color: accentColor }} />
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Outgoing queue ({totalCount})
        </span>
      </div>

      {expenseActions.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/60 dark:bg-amber-900/20">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1.5">
            Expense approvals ({expenseActions.length})
          </p>
          <ul className="space-y-1">
            {expenseActions.slice(0, 5).map((action) => (
              <li key={`${action.type}-${action.expenseId}`} className="flex items-center justify-between text-xs text-amber-900 dark:text-amber-100">
                <span className="truncate">
                  <span className="font-medium capitalize">{action.type}</span> · {action.expenseId}
                </span>
                <span className="text-amber-600 dark:text-amber-400 whitespace-nowrap tabular-nums">
                  {new Date(action.createdAt).toLocaleDateString()}
                </span>
              </li>
            ))}
            {expenseActions.length > 5 && (
              <li className="text-xs text-amber-600 dark:text-amber-400">
                +{expenseActions.length - 5} more…
              </li>
            )}
          </ul>
        </div>
      )}

      {formSubmissions.length > 0 && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-800/60 dark:bg-blue-900/20">
          <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 mb-1.5">
            Form submissions ({formSubmissions.length})
          </p>
          <ul className="space-y-1">
            {formSubmissions.slice(0, 5).map((sub) => (
              <li key={sub.id} className="flex items-center justify-between text-xs text-blue-900 dark:text-blue-100">
                <span className="truncate">
                  {sub.formKey}
                  {sub.workItemId ? ` · ${sub.workItemId}` : ''}
                  {sub.retries > 0 && <span className="text-blue-500 dark:text-blue-400 ml-1">(retry {sub.retries})</span>}
                </span>
                <span className="text-blue-600 dark:text-blue-400 whitespace-nowrap tabular-nums">
                  {new Date(sub.queuedAt).toLocaleDateString()}
                </span>
              </li>
            ))}
            {formSubmissions.length > 5 && (
              <li className="text-xs text-blue-600 dark:text-blue-400">
                +{formSubmissions.length - 5} more…
              </li>
            )}
          </ul>
        </div>
      )}

      <p className="text-xs text-slate-500 dark:text-slate-400 px-1">
        These items will upload automatically when you reconnect.
      </p>
    </div>
  );
}

export function MasterSyncPreparationModal({
  isOpen,
  status,
  progress,
  offlinePreparationMode = 'auto',
  onClose,
  onStart,
  onRetry,
  onOfflinePreparationModeChange,
  startDisabled = false,
  startLabel = 'Download offline data',
  startTitle,
  accentColor = '#3B82F6',
  title = 'Prepare for Offline Mode',
  preparingSubtitle = 'Syncing all required data for offline operation',
  readySubtitle = 'Device can operate offline',
  errorSubtitle = 'Some components need attention',
}: MasterSyncPreparationModalProps) {
  const { status: globalSyncStatus } = useSyncStatus();
  const outgoingQueue = useOutgoingQueue(isOpen);

  if (!isOpen) {
    return null;
  }

  const isCapacitorApp = isCapacitor();
  const showActivitySection =
    globalSyncStatus === 'syncing' || (status === 'preparing' && progress);

  const headerTitle = status === 'ready'
    ? 'Ready for Offline!'
    : status === 'error'
      ? 'Partial Readiness'
      : status === 'preparing'
        ? 'Preparing...'
        : title;

  const subtitle = status === 'ready'
    ? readySubtitle
    : status === 'error'
      ? errorSubtitle
      : preparingSubtitle;

  const progressPercent = progress && progress.total > 0
    ? (progress.current / progress.total) * 100
    : 0;
  const showDevDebug = process.env.NODE_ENV === 'development';
  const debugSummary = getOfflinePreparationDebugSummary(progress);
  const smartClockDebugCount = (debugSummary?.attendanceData || 0) + (debugSummary?.pages || 0) + (debugSummary?.faceModels || 0);
  const smartTaskDebugCount = (debugSummary?.workItems || 0) + (debugSummary?.forms || 0) + (debugSummary?.vehicles || 0);

  return (
    <>
      <div className={`fixed inset-0 z-[130] flex justify-center bg-slate-950/60 backdrop-blur-md ${isCapacitorApp ? 'items-stretch p-0' : 'items-center p-4'}`}>
        <div
          className={`overflow-hidden flex flex-col bg-white shadow-2xl dark:bg-slate-900 ${isCapacitorApp
            ? 'h-[100dvh] w-screen rounded-none border-0'
            : 'max-h-[85vh] w-[90vw] max-w-lg rounded-[28px] border border-white/10 dark:border-slate-700'}`}
        >
          <div
            className="flex-shrink-0 border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
            style={{
              paddingTop: isCapacitorApp ? '1rem' : undefined,
            }}
          >
            {/* Header row */}
            <div className="mb-1 px-4 pt-4 pb-3 relative">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
                  {status === 'preparing' ? (
                    <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 text-white animate-spin" />
                  ) : status === 'ready' ? (
                    <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  ) : status === 'error' ? (
                    <AlertCircle className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  ) : (
                    <Plane className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  )}
                </div>
                <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-slate-900 dark:text-white">{headerTitle}</h1>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
              </div>
              <button
                onClick={onClose}
                className="absolute right-4 top-4 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
                aria-label="Close offline preparation modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* BrandedLoadingSpinner-style animated progress header — shown while preparing */}
            {status === 'preparing' && (
              <div className="px-4 pb-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/90 px-4 py-3">
                  <div className="flex items-center gap-3 mb-3">
                    {/* Spinner ring with logo */}
                    <div className="relative flex-shrink-0" style={{ width: 36, height: 36 }}>
                      <div className="absolute inset-0 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <Image
                          src="/logo/total-access-icon.png"
                          alt="TotalAccess"
                          width={22}
                          height={22}
                          className="object-contain"
                          priority
                          unoptimized
                        />
                      </div>
                      <div
                        className="absolute inset-0 rounded-full animate-spin"
                        style={{
                          borderWidth: 2,
                          borderStyle: 'solid',
                          borderColor: 'transparent',
                          borderTopColor: 'rgba(59,130,246,0.95)',
                          borderRightColor: 'rgba(59,130,246,0.4)',
                          animationDuration: '1s',
                          animationTimingFunction: 'linear',
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {progress?.step || 'Preparing offline data…'}
                      </p>
                      {progress && progress.total > 0 && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {progress.current} of {progress.total} {progress.total === 1 ? 'step' : 'steps'} complete
                        </p>
                      )}
                    </div>
                    {progress && progress.total > 0 && (
                      <span className="text-xs font-bold text-blue-600 dark:text-blue-300 tabular-nums flex-shrink-0">
                        {Math.round((progress.current / progress.total) * 100)}%
                      </span>
                    )}
                  </div>

                  {/* Determinate progress bar matching BrandedLoadingSpinner style */}
                  <div className="relative h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{
                        width: `${progressPercent}%`,
                        background: 'linear-gradient(90deg, rgba(59,130,246,0.75) 0%, rgba(59,130,246,0.95) 55%, rgba(59,130,246,0.8) 100%)',
                        transition: 'width 500ms ease-out',
                        boxShadow: '0 0 12px rgba(59,130,246,0.35)',
                      }}
                    />
                    {/* Shimmer */}
                    <div className="loading-progress-shimmer pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-transparent via-blue-200/70 to-transparent mix-blend-screen" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className="p-6 overflow-y-auto flex-1"
            style={{
              paddingBottom: isCapacitorApp ? `calc(${MOBILE_SAFE_AREA_BOTTOM} + 1.5rem)` : undefined,
            }}
          >
            {showActivitySection && (
              <section className="mb-6 space-y-3" aria-label="Current sync activity">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Current activity
                </p>

                {globalSyncStatus === 'syncing' && (
                  <MasterSyncUploadProgressPanel variant="card" />
                )}

                {status === 'preparing' && progress && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/90 overflow-hidden p-4">
                    <div className="flex items-start gap-3">
                      <Loader2 className="h-4 w-4 animate-spin flex-shrink-0 mt-0.5" style={{ color: accentColor }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                          Offline download
                        </p>
                        <p className="text-sm text-slate-800 dark:text-slate-100 break-words">
                          {progress.step || 'Preparing offline data…'}
                        </p>
                        {progress.total > 0 && (
                          <>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                              {progress.current} of {progress.total} {progress.total === 1 ? 'step' : 'steps'}
                            </p>
                            <div className="mt-2 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-300"
                                style={{
                                  width: `${(progress.current / progress.total) * 100}%`,
                                  backgroundColor: accentColor,
                                }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}

            <OutgoingQueueSection
              formSubmissions={outgoingQueue.formSubmissions}
              expenseActions={outgoingQueue.expenseActions}
              loading={outgoingQueue.loading}
              accentColor={accentColor}
            />

            {status === 'idle' && (
              <div className="mb-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
                <p className="font-medium text-slate-900 dark:text-white">Download what you need before you go offline.</p>
                <p className="mt-2 leading-6 text-slate-600 dark:text-slate-300">
                  This will cache your available offline attendance and SmartTask data on the device so you can keep working when signal disappears.
                </p>
              </div>
            )}

            <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/80">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Offline download mode</p>
                  <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    Choose whether offline data downloads automatically on the dashboard or only when you start it yourself.
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${offlinePreparationMode === 'auto'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                  : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>
                  {offlinePreparationMode === 'auto' ? 'Auto' : 'Manual'}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => onOfflinePreparationModeChange?.('auto')}
                  className={`rounded-2xl border px-4 py-3 text-left transition-all ${offlinePreparationMode === 'auto'
                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600'}`}
                >
                  <div className="text-sm font-semibold">Automatic</div>
                  <div className="mt-1 text-xs leading-5 opacity-80">Prepare offline data in the background after landing on the dashboard.</div>
                </button>
                <button
                  type="button"
                  onClick={() => onOfflinePreparationModeChange?.('manual')}
                  className={`rounded-2xl border px-4 py-3 text-left transition-all ${offlinePreparationMode === 'manual'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm dark:border-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-200'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600'}`}
                >
                  <div className="text-sm font-semibold">Manual only</div>
                  <div className="mt-1 text-xs leading-5 opacity-80">Disable background downloads and only prepare offline data when you tap the button.</div>
                </button>
              </div>
            </div>

            {progress && (
              <>

                <div className="mb-6 space-y-3">
                  {progress.details.map((detail, index) => (
                    <div
                      key={`${detail.label}-${index}`}
                      className={`flex items-center justify-between rounded-2xl border p-3 ${detail.status === 'error'
                          ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                          : detail.status === 'pending'
                            ? 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50'
                            : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        {detail.status === 'loading' ? (
                          <Loader2 className="h-5 w-5 animate-spin" style={{ color: accentColor }} />
                        ) : detail.status === 'success' ? (
                          <CheckCircle className="h-5 w-5" style={{ color: accentColor }} />
                        ) : detail.status === 'error' ? (
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-slate-300 dark:border-slate-500" />
                        )}
                        <span className={`font-medium ${detail.status === 'pending' ? 'text-slate-500 dark:text-slate-400' : 'text-slate-800 dark:text-slate-100'}`}>
                          {detail.label}
                        </span>
                      </div>
                      {detail.count !== undefined && (
                        <span
                          className={`rounded-lg px-2 py-1 text-sm font-semibold ${detail.status === 'error'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                              : detail.status === 'pending'
                                ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                : ''
                            }`}
                          style={detail.status === 'success' || detail.status === 'loading'
                            ? { backgroundColor: `${accentColor}20`, color: accentColor }
                            : undefined}
                        >
                          {detail.count}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {showDevDebug && debugSummary && (
                  <div className="mb-6 rounded-3xl border border-dashed border-slate-300 bg-slate-50/90 p-4 text-xs text-slate-700 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-200">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white">Dev download summary</p>
                        <p className="mt-1 text-[11px] leading-5 text-slate-500 dark:text-slate-400">
                          Visible in development only. Handy for checking what SmartClock and SmartTask actually cached.
                        </p>
                      </div>
                      <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white dark:bg-slate-100 dark:text-slate-900">
                        Dev only
                      </span>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/60 dark:bg-blue-950/30">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-blue-800 dark:text-blue-200">SmartClock</span>
                          <span className="rounded-lg bg-blue-100 px-2 py-1 font-semibold text-blue-700 dark:bg-blue-900/60 dark:text-blue-200">
                            {smartClockDebugCount}
                          </span>
                        </div>
                        <div className="mt-2 space-y-1 text-[11px] text-blue-900/80 dark:text-blue-100/80">
                          <div>Attendance data: {debugSummary.attendanceData}</div>
                          <div>Attendance pages: {debugSummary.pages}</div>
                          <div>Face models: {debugSummary.faceModels}</div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/60 dark:bg-emerald-950/30">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-emerald-800 dark:text-emerald-200">SmartTask</span>
                          <span className="rounded-lg bg-emerald-100 px-2 py-1 font-semibold text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200">
                            {smartTaskDebugCount}
                          </span>
                        </div>
                        <div className="mt-2 space-y-1 text-[11px] text-emerald-900/80 dark:text-emerald-100/80">
                          <div>Assigned work items: {debugSummary.workItems}</div>
                          <div>Operational forms: {debugSummary.forms}</div>
                          <div>Fleet vehicles: {debugSummary.vehicles}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

          </div>

          {/* Bottom action bar — outside scrollable area */}
          <div
            className="flex flex-shrink-0 justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4 dark:border-slate-700 dark:bg-slate-900"
            style={isCapacitorApp
              ? { paddingBottom: `calc(${MOBILE_SAFE_AREA_BOTTOM} + 1rem)` }
              : undefined}
          >
            {status === 'idle' && onStart && (
              <button
                onClick={onStart}
                disabled={startDisabled}
                title={startTitle}
                className="rounded-2xl px-6 py-3 font-semibold text-white transition-all enabled:hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                style={{ backgroundColor: accentColor }}
              >
                {startLabel}
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-2xl px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: accentColor }}
            >
              {status === 'ready' ? 'Done' : status === 'preparing' ? 'Close (continue in background)' : 'Close'}
            </button>
            {status === 'error' && onRetry && (
              <button
                onClick={onRetry}
                className="flex items-center gap-2 rounded-2xl px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: accentColor }}
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`
      @keyframes progressShimmer {
        0% { transform: translateX(-130%); opacity: 0; }
        18% { opacity: 0.9; }
        100% { transform: translateX(360%); opacity: 0; }
      }
      .loading-progress-shimmer {
        animation: progressShimmer 1.9s ease-out infinite;
      }
    `}</style>
    </>
  );
}

export function MasterSyncStatusBadge({
  className = '',
  showLabel = true,
  size = 'md',
  onClick,
}: MasterSyncStatusBadgeProps) {
  const { status, totalPending, progress } = useSyncStatus();
  const isOnline = networkState.isOnline();

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const paddingSizes = {
    sm: 'px-1.5 py-0.5',
    md: 'px-2 py-1',
    lg: 'px-3 py-1.5',
  };

  let icon: React.ReactNode;
  let label: string;
  let bgColor: string;
  let textColor: string;

  if (!isOnline) {
    icon = <CloudOff className={iconSizes[size]} />;
    label = 'Offline';
    bgColor = 'bg-gray-100 dark:bg-gray-800';
    textColor = 'text-gray-600 dark:text-gray-400';
  } else if (status === 'syncing') {
    icon = <RefreshCw className={`${iconSizes[size]} animate-spin`} />;
    label = progress.total ? `Syncing ${progress.uploaded || 0}/${progress.total}` : 'Syncing...';
    bgColor = 'bg-blue-100 dark:bg-blue-900/30';
    textColor = 'text-blue-600 dark:text-blue-400';
  } else if (totalPending > 0) {
    icon = <Cloud className={iconSizes[size]} />;
    label = `${totalPending} pending`;
    bgColor = 'bg-yellow-100 dark:bg-yellow-900/30';
    textColor = 'text-yellow-600 dark:text-yellow-400';
  } else {
    icon = <Check className={iconSizes[size]} />;
    label = 'Synced';
    bgColor = 'bg-green-100 dark:bg-green-900/30';
    textColor = 'text-green-600 dark:text-green-400';
  }

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full ${paddingSizes[size]} ${bgColor} ${textColor} ${textSizes[size]} font-medium transition-colors hover:opacity-80 ${onClick ? 'cursor-pointer' : 'cursor-default'} ${className}`}
    >
      {icon}
      {showLabel && <span>{label}</span>}
    </button>
  );
}

/**
 * Live upload / cloud sync progress (SyncStatusContext). Used in the app header strip and inside
 * MasterSyncPreparationModal so the pill and modal stay aligned.
 */
export function MasterSyncUploadProgressPanel({
  className = '',
  variant = 'banner',
}: MasterSyncUploadProgressPanelProps) {
  const { status, progress, stats } = useSyncStatus();

  const completed = Math.max(0, progress.uploaded || 0);
  const total = Math.max(0, progress.total || 0);
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const latestOperationSteps = stats.lastOperationsSyncStepResults;
  const recentOperationFailures = useMemo(
    () => latestOperationSteps.filter((step) => step.failed > 0 || step.errors.length > 0),
    [latestOperationSteps],
  );
  const slowestRecentOperationSteps = useMemo(
    () => [...latestOperationSteps].sort((left, right) => right.durationMs - left.durationMs).slice(0, 3),
    [latestOperationSteps],
  );

  if (status !== 'syncing') {
    return null;
  }

  const formatStepDuration = (durationMs: number): string => {
    if (!Number.isFinite(durationMs) || durationMs <= 0) {
      return '<1s';
    }

    if (durationMs < 1000) {
      return '<1s';
    }

    const seconds = durationMs / 1000;
    if (seconds < 60) {
      return `${seconds.toFixed(seconds >= 10 ? 0 : 1)}s`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const body = (
    <div
      className={variant === 'card' ? 'px-4 py-3 flex items-start gap-3' : 'px-4 py-2 flex items-start gap-3'}
      role="status"
      aria-live="polite"
      aria-label={progress.message || 'Syncing changes'}
    >
      <RefreshCw className="h-4 w-4 animate-spin text-blue-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        {variant === 'card' && (
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Server sync
          </p>
        )}
        <div className="flex justify-between text-sm mb-1 gap-3">
          <span className="text-gray-600 dark:text-gray-400 truncate">
            {progress.message || 'Syncing changes...'}
          </span>
          <span className="text-gray-500 dark:text-gray-500 whitespace-nowrap tabular-nums">
            {completed}/{total || '?'}
          </span>
        </div>
        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${percentage}%` }} />
        </div>

        {recentOperationFailures.length > 0 && progress.currentModule === 'operations' && (
          <div className="mt-2 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <div className="font-medium">Latest operations sync issues</div>
            <ul className="mt-1 space-y-0.5">
              {recentOperationFailures.slice(0, 3).map((step) => (
                <li key={step.key} className="truncate">
                  {step.label} ({formatStepDuration(step.durationMs)}): {step.errors[0] || `${step.failed} failed item${step.failed === 1 ? '' : 's'}`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {recentOperationFailures.length === 0 && progress.currentModule === 'operations' && slowestRecentOperationSteps.length > 0 && (
          <div className="mt-2 rounded-md bg-slate-50 px-2.5 py-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
            <div className="font-medium">Latest operations step timings</div>
            <ul className="mt-1 space-y-0.5">
              {slowestRecentOperationSteps.map((step) => (
                <li key={step.key} className="truncate">
                  {step.label}: {formatStepDuration(step.durationMs)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  if (variant === 'card') {
    return (
      <div
        className={`rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/90 overflow-hidden ${className}`}
      >
        {body}
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-900 border-b dark:border-gray-800 ${className}`}>
      {body}
    </div>
  );
}

export function MasterSyncProgressBar({ className = '' }: MasterSyncProgressBarProps) {
  return <MasterSyncUploadProgressPanel variant="banner" className={className} />;
}

export function MasterSyncOfflineBanner({ className = '' }: MasterSyncOfflineBannerProps) {
  const { totalPending } = useSyncStatus();
  const isOnline = networkState.isOnline();

  if (isOnline) {
    return null;
  }

  return (
    <div className={`bg-gray-800 text-white px-4 py-2 flex items-center justify-center gap-2 ${className}`}>
      <CloudOff className="h-4 w-4" />
      <span className="text-sm font-medium">
        You're offline
        {totalPending > 0 && ` • ${totalPending} changes will sync when connected`}
      </span>
    </div>
  );
}

export function useMasterSyncModal({
  mounted,
  isAuthenticated,
  isOnDashboard,
  networkStatus,
  userId,
  autoPreparationClientId,
  autoDelayMs = AUTO_OFFLINE_PREP_DELAY_MS,
  autoFreshnessMs = AUTO_OFFLINE_PREP_FRESHNESS_MS,
  autoRetryMs = AUTO_OFFLINE_PREP_RETRY_MS,
}: UseMasterSyncModalOptions): MasterSyncModalController {
  const {
    status: syncStatus,
    cancelSync,
    refreshStats,
    moduleAccess,
  } = useSyncStatus();
  const { registerBackHandler, unregisterBackHandler } = useModalState();
  const offlinePreparation = useMasterSyncOfflinePreparation();
  const autoOfflinePreparationTimerRef = useRef<number | null>(null);
  const [offlinePreparationModeOverride, setOfflinePreparationModeOverride] = useState<{
    storageKey: string | null;
    mode: OfflinePreparationMode;
  } | null>(null);

  const canPrepareOffline = moduleAccess.attendance || moduleAccess.operations;

  const offlinePreparationSummary = useMemo<OfflinePreparationSummary>(() => {
    if (moduleAccess.attendance && moduleAccess.operations) {
      return {
        preparingSubtitle: 'Caching attendance and SmartTask data for offline use',
        readySubtitle: 'Attendance and SmartTask are ready offline',
      };
    }

    if (moduleAccess.attendance) {
      return {
        preparingSubtitle: 'Caching attendance data and face recognition assets',
        readySubtitle: 'Attendance is ready offline',
      };
    }

    return {
      preparingSubtitle: 'Caching assigned SmartTask data for offline use',
      readySubtitle: 'SmartTask is ready offline',
    };
  }, [moduleAccess.attendance, moduleAccess.operations]);

  const autoOfflinePreparationStorageKey = useMemo(() => {
    const numericUserId = Number(userId || 0);
    const numericClientId = Number(autoPreparationClientId || 0);

    if (!numericUserId || numericClientId <= 0 || !canPrepareOffline) {
      return null;
    }

    const modules = [
      moduleAccess.attendance ? 'attendance' : null,
      moduleAccess.operations ? 'operations' : null,
    ].filter(Boolean).join('-');

    return `${AUTO_OFFLINE_PREP_STORAGE_PREFIX}:${numericUserId}:${numericClientId}:${modules || 'none'}`;
  }, [autoPreparationClientId, canPrepareOffline, moduleAccess.attendance, moduleAccess.operations, userId]);

  const autoOfflinePreparationModeStorageKey = useMemo(() => {
    const numericUserId = Number(userId || 0);
    const numericClientId = Number(autoPreparationClientId || 0);

    if (!numericUserId || numericClientId <= 0 || !canPrepareOffline) {
      return null;
    }

    const modules = [
      moduleAccess.attendance ? 'attendance' : null,
      moduleAccess.operations ? 'operations' : null,
    ].filter(Boolean).join('-');

    return `${AUTO_OFFLINE_PREP_MODE_STORAGE_PREFIX}:${numericUserId}:${numericClientId}:${modules || 'none'}`;
  }, [autoPreparationClientId, canPrepareOffline, moduleAccess.attendance, moduleAccess.operations, userId]);

  const storedOfflinePreparationMode = useMemo<OfflinePreparationMode>(() => {
    if (typeof window === 'undefined' || !autoOfflinePreparationModeStorageKey) {
      return 'auto';
    }

    try {
      const storedMode = window.localStorage.getItem(autoOfflinePreparationModeStorageKey);
      return storedMode === 'manual' ? 'manual' : 'auto';
    } catch (error) {
      console.warn('[MasterSyncModal] Failed to read offline preparation mode:', error);
      return 'auto';
    }
  }, [autoOfflinePreparationModeStorageKey]);

  const offlinePreparationMode = offlinePreparationModeOverride?.storageKey === autoOfflinePreparationModeStorageKey
    ? offlinePreparationModeOverride.mode
    : storedOfflinePreparationMode;

  const setOfflinePreparationMode = useCallback((mode: OfflinePreparationMode) => {
    setOfflinePreparationModeOverride({
      storageKey: autoOfflinePreparationModeStorageKey,
      mode,
    });

    if (typeof window === 'undefined' || !autoOfflinePreparationModeStorageKey) {
      return;
    }

    try {
      window.localStorage.setItem(autoOfflinePreparationModeStorageKey, mode);
    } catch (error) {
      console.warn('[MasterSyncModal] Failed to persist offline preparation mode:', error);
    }
  }, [autoOfflinePreparationModeStorageKey]);

  const readOfflinePreparationState = useCallback((): OfflinePreparationState | null => {
    if (typeof window === 'undefined' || !autoOfflinePreparationStorageKey) {
      return null;
    }

    try {
      const rawState = window.localStorage.getItem(autoOfflinePreparationStorageKey);
      if (!rawState) {
        return null;
      }

      const parsedState = JSON.parse(rawState);
      return {
        lastAttemptAt: Number(parsedState?.lastAttemptAt || 0),
        completedAt: Number(parsedState?.completedAt || 0),
        lastResult: parsedState?.lastResult === 'success'
          ? 'success'
          : parsedState?.lastResult === 'error'
            ? 'error'
            : null,
      };
    } catch (error) {
      console.warn('[MasterSyncModal] Failed to read offline preparation state:', error);
      return null;
    }
  }, [autoOfflinePreparationStorageKey]);

  const persistOfflinePreparationState = useCallback((result: 'attempt' | 'success' | 'error') => {
    if (typeof window === 'undefined' || !autoOfflinePreparationStorageKey) {
      return;
    }

    const now = Date.now();
    const previousState = readOfflinePreparationState();
    const nextState = {
      lastAttemptAt: now,
      completedAt: result === 'success' ? now : previousState?.completedAt || 0,
      lastResult: result === 'attempt' ? previousState?.lastResult || null : result,
    };

    try {
      window.localStorage.setItem(autoOfflinePreparationStorageKey, JSON.stringify(nextState));
    } catch (error) {
      console.warn('[MasterSyncModal] Failed to persist offline preparation state:', error);
    }
  }, [autoOfflinePreparationStorageKey, readOfflinePreparationState]);

  const shouldAutoPrepareOffline = useCallback(() => {
    if (!autoOfflinePreparationStorageKey || typeof window === 'undefined') {
      return false;
    }

    if (document.visibilityState === 'hidden') {
      return false;
    }

    const currentState = readOfflinePreparationState();
    if (!currentState) {
      return true;
    }

    const now = Date.now();
    if (currentState.completedAt > 0 && now - currentState.completedAt < autoFreshnessMs) {
      return false;
    }

    if (currentState.lastAttemptAt > 0 && now - currentState.lastAttemptAt < autoRetryMs) {
      return false;
    }

    return true;
  }, [autoFreshnessMs, autoOfflinePreparationStorageKey, autoRetryMs, readOfflinePreparationState]);

  const buildOfflinePreparationPlan = useCallback((options?: { includeAttendanceAssets?: boolean }) => {
    const includeAttendanceAssets = options?.includeAttendanceAssets !== false;
    const detailIndex: OfflinePreparationDetailIndex = {};
    const details: Array<{ label: string }> = [];

    if (moduleAccess.attendance) {
      detailIndex.attendance = details.length;
      details.push({ label: 'Attendance data' });
    }

    if (moduleAccess.operations) {
      detailIndex.workItems = details.length;
      details.push({ label: 'Assigned work items' });
      detailIndex.forms = details.length;
      details.push({ label: 'Operational forms' });
      detailIndex.vehicles = details.length;
      details.push({ label: 'Fleet vehicles' });
    }

    if (moduleAccess.attendance && includeAttendanceAssets) {
      detailIndex.pages = details.length;
      details.push({ label: 'Attendance pages' });
      detailIndex.faceModels = details.length;
      details.push({ label: 'Face recognition models' });
    }

    return { detailIndex, details };
  }, [moduleAccess.attendance, moduleAccess.operations]);

  const executeOfflinePreparation = useCallback(async (
    detailIndex: OfflinePreparationDetailIndex,
    updateProgress: (step: string, index: number, status: 'loading' | 'success' | 'error', count?: number) => void,
  ) => {
    // Mark each known step as loading before the unified prepare call so the UI
    // shows which modules are actively downloading (not stuck on 'pending').
    if (detailIndex.attendance !== undefined) {
      updateProgress('Downloading attendance data…', detailIndex.attendance, 'loading');
    }
    if (detailIndex.workItems !== undefined) {
      updateProgress('Caching assigned work items…', detailIndex.workItems, 'loading');
    }
    if (detailIndex.forms !== undefined) {
      updateProgress('Downloading operational forms…', detailIndex.forms, 'loading');
    }
    if (detailIndex.vehicles !== undefined) {
      updateProgress('Caching fleet vehicles…', detailIndex.vehicles, 'loading');
    }
    let prepareResult = await masterSync.prepareOffline({
      attendanceMode: moduleAccess.attendance ? 'auto' : undefined,
    });

    if (!prepareResult.success && prepareResult.errors.some(isTransientOfflinePreparationError)) {
      console.warn(`[MasterSyncModal] Offline preparation hit transient sync/preparation lock. Retrying shortly... ${serializeOfflinePrepLog({
        errors: prepareResult.errors,
      })}`);
      await new Promise((resolve) => setTimeout(resolve, OFFLINE_PREP_TRANSIENT_RETRY_DELAY_MS));
      prepareResult = await masterSync.prepareOffline({
        attendanceMode: moduleAccess.attendance ? 'auto' : undefined,
      });
    }

    if (!prepareResult.success) {
      console.error(`[MasterSyncModal] masterSync.prepareOffline failed: ${serializeOfflinePrepLog({
        errors: prepareResult.errors,
        attendance: prepareResult.attendance,
        operations: prepareResult.operations,
      })}`);
    }

    if (detailIndex.attendance !== undefined) {
      const attendanceCount = (prepareResult.attendance?.downloadedEmployees || 0)
        + (prepareResult.attendance?.downloadedTemplates || 0)
        + (prepareResult.attendance?.downloadedShifts || 0)
        + (prepareResult.attendance?.downloadedGeofences || 0);

      updateProgress(
        'Attendance data ready',
        detailIndex.attendance,
        prepareResult.attendance ? 'success' : 'error',
        attendanceCount,
      );
    }

    const hasAssignedWorkItemsError = prepareResult.errors.some((error) => /assigned work items/i.test(String(error || '')));

    if (detailIndex.workItems !== undefined) {
      updateProgress(
        'Assigned work items cached',
        detailIndex.workItems,
        hasAssignedWorkItemsError ? 'error' : (prepareResult.operations ? 'success' : 'error'),
        prepareResult.operations?.cachedWorkItems || 0,
      );
    }

    if (detailIndex.forms !== undefined) {
      const formsFailed = prepareResult.operations?.failedForms || 0;
      updateProgress(
        'Operational forms cached',
        detailIndex.forms,
        hasAssignedWorkItemsError || formsFailed > 0 ? 'error' : 'success',
        prepareResult.operations?.cachedForms || 0,
      );
    }

    const hasFleetVehiclesError = prepareResult.errors.some((error) => /fleet vehicles/i.test(String(error || '')));
    if (detailIndex.vehicles !== undefined) {
      updateProgress(
        'Fleet vehicles cached',
        detailIndex.vehicles,
        hasFleetVehiclesError ? 'error' : (prepareResult.operations ? 'success' : 'error'),
        prepareResult.operations?.cachedVehicles || 0,
      );
    }

    // Attendance pages and face model files require a separate step: service worker
    // page precaching + face-api model binary downloads.
    let assetError: string | null = null;
    if (detailIndex.pages !== undefined && detailIndex.faceModels !== undefined) {
      const assetResult = await prepareAttendanceOfflineAssets({
        pageStepIndex: detailIndex.pages,
        modelStepIndex: detailIndex.faceModels,
        updateProgress,
        logPrefix: '[MasterSyncModal]',
      });
      if (assetResult.error) {
        assetError = assetResult.error;
      }
    }

    // Fire-and-forget: refreshStats touches Capacitor native bridge and can stall.
    // The task must complete promptly so the modal transitions out of 'preparing'.
    void refreshStats().catch(() => undefined);

    const cacheErrorMessage = prepareResult.errors.length > 0
      ? prepareResult.errors.join(' | ')
      : assetError || null;
    if (cacheErrorMessage) {
      console.error(`[MasterSyncModal] Offline preparation ended with cache/asset errors: ${serializeOfflinePrepLog({
        cacheErrorMessage,
        prepareErrors: prepareResult.errors,
        assetError,
      })}`);
      throw new Error(cacheErrorMessage);
    }
  }, [moduleAccess.attendance, refreshStats]);

  const runOfflinePreparationFlow = useCallback(async ({
    openModal,
    showNotifications,
  }: {
    openModal: boolean;
    showNotifications: boolean;
  }): Promise<boolean> => {
    if (offlinePreparation.status === 'preparing') {
      return false;
    }

    if (!canPrepareOffline) {
      if (showNotifications) {
        notify.error('No offline-ready modules are enabled for this account right now.');
      }
      return false;
    }

    if (!networkState.isOnline()) {
      if (showNotifications) {
        notify.error('Go online first so we can cache your offline data.');
      }
      return false;
    }

    if (masterSync.isSyncInProgress()) {
      if (!openModal && !showNotifications) {
        return false;
      }

      cancelSync();
      await refreshStats().catch(() => undefined);

      if (showNotifications) {
        notify.info('Paused the current sync so we can prioritise your offline download.');
      }
    }

    // On Capacitor and installed PWAs, always include attendance assets (face
    // models + pages) in auto-prepare so personal-mode users can clock in
    // offline without having manually opened the MasterSync modal.
    // On plain web, only include them when the modal is open (user-initiated).
    const includeAttendanceAssets = openModal || showNotifications || isCapacitor() || isStandalonePwa();
    const plan = buildOfflinePreparationPlan({ includeAttendanceAssets });
    if (plan.details.length === 0) {
      return false;
    }

    persistOfflinePreparationState('attempt');

    const wasSuccessful = await offlinePreparation.runPreparation({
      openModal,
      details: plan.details,
      task: async ({ updateProgress }) => {
        console.log(`[MasterSyncModal] Starting offline preparation flow ${serializeOfflinePrepLog({
          openModal,
          showNotifications,
          includeAttendanceAssets,
          canPrepareOffline,
          attendanceEnabled: Boolean(plan.detailIndex.attendance !== undefined),
          operationsEnabled: Boolean(plan.detailIndex.workItems !== undefined),
        })}`);
        await executeOfflinePreparation(plan.detailIndex, updateProgress);
      },
    });

    persistOfflinePreparationState(wasSuccessful ? 'success' : 'error');

    if (showNotifications) {
      if (wasSuccessful) {
        notify.success('Offline data is ready to travel with you.');
      } else {
        notify.error('Offline preparation finished with issues. Review the checklist and retry if needed.');
      }
    }

    return wasSuccessful;
  }, [buildOfflinePreparationPlan, cancelSync, canPrepareOffline, executeOfflinePreparation, offlinePreparation, persistOfflinePreparationState, refreshStats]);

  const openModal = useCallback(() => {
    if (!canPrepareOffline) {
      return;
    }

    offlinePreparation.setOpen(true);
  }, [canPrepareOffline, offlinePreparation]);

  const closeModal = useCallback(() => {
    offlinePreparation.closeModal();
  }, [offlinePreparation]);

  const startPreparation = useCallback(async () => runOfflinePreparationFlow({
    openModal: true,
    showNotifications: true,
  }), [runOfflinePreparationFlow]);

  useEffect(() => {
    if (!offlinePreparation.isOpen) {
      return;
    }

    const handleOfflinePreparationBackPress = () => {
      if (offlinePreparation.status === 'preparing') {
        return true;
      }

      offlinePreparation.closeModal();
      return true;
    };

    registerBackHandler('master-sync-offline-preparation', handleOfflinePreparationBackPress);

    return () => {
      unregisterBackHandler('master-sync-offline-preparation');
    };
  }, [offlinePreparation, registerBackHandler, unregisterBackHandler]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (autoOfflinePreparationTimerRef.current) {
      clearTimeout(autoOfflinePreparationTimerRef.current);
      autoOfflinePreparationTimerRef.current = null;
    }

    if (!mounted || !isAuthenticated || !canPrepareOffline) {
      return;
    }

    if (offlinePreparationMode !== 'auto') {
      return;
    }

    if (syncStatus === 'syncing' || offlinePreparation.status === 'preparing') {
      return;
    }

    if (!networkState.isOnline() || !shouldAutoPrepareOffline()) {
      return;
    }

    autoOfflinePreparationTimerRef.current = window.setTimeout(() => {
      void runOfflinePreparationFlow({
        openModal: false,
        showNotifications: false,
      });
    }, autoDelayMs);

    return () => {
      if (autoOfflinePreparationTimerRef.current) {
        clearTimeout(autoOfflinePreparationTimerRef.current);
        autoOfflinePreparationTimerRef.current = null;
      }
    };
  }, [autoDelayMs, canPrepareOffline, isAuthenticated, mounted, offlinePreparation.status, offlinePreparationMode, runOfflinePreparationFlow, shouldAutoPrepareOffline, syncStatus]);

  return {
    canPrepareOffline,
    isOpen: offlinePreparation.isOpen,
    status: offlinePreparation.status,
    progress: offlinePreparation.progress,
    offlinePreparationMode,
    openModal,
    closeModal,
    startPreparation,
    setOfflinePreparationMode,
    startDisabled: !canPrepareOffline || !networkState.isOnline() || offlinePreparation.status === 'preparing',
    startLabel: offlinePreparation.status === 'preparing' ? 'Preparing offline data…' : 'Download offline data',
    startTitle: !canPrepareOffline
      ? 'Offline downloads are not enabled for this account'
      : !networkState.isOnline()
        ? 'Go online to prepare offline data'
        : syncStatus === 'syncing' || networkStatus === 'syncing'
          ? 'Download attendance and SmartTask data now and pause the current sync if needed'
          : 'Download attendance and SmartTask data for offline use',
    preparingSubtitle: offlinePreparationSummary.preparingSubtitle,
    readySubtitle: offlinePreparationSummary.readySubtitle,
  };
}

export default function MasterSyncModal({
  controller,
  accentColor = '#047cde',
  title = 'Prepare Offline',
  errorSubtitle = 'Some offline components still need attention',
}: MasterSyncModalProps) {
  return (
    <MasterSyncPreparationModal
      isOpen={controller.isOpen}
      status={controller.status}
      progress={controller.progress}
      offlinePreparationMode={controller.offlinePreparationMode}
      onClose={controller.closeModal}
      onStart={controller.startPreparation}
      onRetry={controller.startPreparation}
      onOfflinePreparationModeChange={controller.setOfflinePreparationMode}
      startDisabled={controller.startDisabled}
      startLabel={controller.startLabel}
      startTitle={controller.startTitle}
      accentColor={accentColor}
      title={title}
      preparingSubtitle={controller.preparingSubtitle}
      readySubtitle={controller.readySubtitle}
      errorSubtitle={errorSubtitle}
    />
  );
}
